import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraButton, SpecraCard, SpecraCardContent, SpecraCardHeader, SpecraEmptyState } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useId, useLayoutEffect, useRef, useState } from 'react';
export function EvidenceAsk({ context, evidence, ask, available = true, mode, onModeChange }) {
    const id = useId();
    const regionRef = useRef(null);
    const [localMode, setLocalMode] = useState('evidence');
    const selected = mode ?? localMode;
    const [askVisited, setAskVisited] = useState(selected === 'ask');
    useLayoutEffect(()=>{
        if (selected === 'ask') setAskVisited(true);
    }, [
        selected
    ]);
    function select(next) {
        if (next === 'ask') setAskVisited(true);
        setLocalMode(next);
        onModeChange?.(next);
    }
    function findDocument() {
        select('evidence');
        // The picker lives in the evidence slot, which is hidden while asking.
        setTimeout(()=>regionRef.current?.querySelector(`[data-testid="${Testids.evidenceAsk.documentSearch}"]`)?.focus(), 0);
    }
    return /*#__PURE__*/ _jsxs(SpecraCard, {
        ref: regionRef,
        className: "min-w-0 overflow-hidden",
        "data-testid": Testids.evidenceAsk.region,
        children: [
            /*#__PURE__*/ _jsx(SpecraCardHeader, {
                className: "min-w-0 flex-col items-stretch break-words",
                children: available ? /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        context.source ? /*#__PURE__*/ _jsxs("p", {
                            className: "break-words",
                            children: [
                                "Selected source: ",
                                context.source.label,
                                " · ",
                                context.source.version,
                                " · Citation:",
                                ' ',
                                context.source.citation
                            ]
                        }) : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                            size: "compact",
                            title: "Pick a document to ground answers",
                            action: /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "secondary",
                                onClick: findDocument,
                                children: "Find document"
                            })
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex flex-wrap gap-2",
                            children: [
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    "aria-pressed": selected === 'evidence',
                                    "aria-controls": `${id}-evidence`,
                                    onClick: ()=>select('evidence'),
                                    "data-testid": Testids.evidenceAsk.evidence,
                                    children: "Evidence"
                                }),
                                /*#__PURE__*/ _jsx(SpecraButton, {
                                    size: "sm",
                                    variant: "secondary",
                                    "aria-pressed": selected === 'ask',
                                    "aria-controls": `${id}-ask`,
                                    onClick: ()=>select('ask'),
                                    "data-testid": Testids.evidenceAsk.ask,
                                    children: "Ask Specra"
                                })
                            ]
                        })
                    ]
                }) : null
            }),
            /*#__PURE__*/ _jsx(SpecraCardContent, {
                children: !available ? /*#__PURE__*/ _jsx("output", {
                    children: "Context is unavailable or outside your access. Continue with the permitted manual workspace."
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            id: `${id}-evidence`,
                            hidden: selected !== 'evidence',
                            "data-testid": Testids.evidenceAsk.source,
                            children: evidence
                        }),
                        /*#__PURE__*/ _jsx("div", {
                            id: `${id}-ask`,
                            hidden: selected !== 'ask',
                            "data-testid": Testids.evidenceAsk.conversation,
                            children: askVisited || selected === 'ask' ? ask : null
                        })
                    ]
                })
            })
        ]
    });
}
export function contextPrompt(context, question) {
    return [
        'Current workspace and fixed write target (inspection does not change this target):',
        JSON.stringify(context),
        'Context includes the selected source, saved pursuit and route filters. Local table filters and unsaved editor text are not included unless supplied by the user.',
        'Use only evidence accessible in this context and cite it. State missing evidence and assumptions. Use calculation tools for arithmetic.',
        'Occupational grouping is not LCAT equivalence; vehicle association is not bid eligibility; a past-performance reference does not prove the solicitation accepts it.',
        'Answer read-only questions immediately. For mutations, identify the object and consequence and use the existing explicit proposal acceptance controls. Do not make commitments, agreements, prices, releases or submissions silently.',
        question
    ].join('\n');
}
const CONTEXT_PROMPT_HEADER = 'Current workspace and fixed write target';
const CONTEXT_PROMPT_LINES = 6; // header, context JSON, then four fixed instruction lines.
const DOCUMENT_PROPOSAL = /^Read document \S+ using read-document-content, then propose improvements using write-to-document\. The proposal target is this document \(([\s\S]*)\), not the pursuit hypothesis\./;
/**
 * What the transcript shows for a user turn: the question alone. The model still
 * receives the whole `contextPrompt`; the record ID, write target and route filters
 * are plumbing, not something the user typed.
 */ export function askDisplayText(text) {
    const lines = text.split('\n');
    const question = lines[0]?.startsWith(CONTEXT_PROMPT_HEADER) ? lines.slice(CONTEXT_PROMPT_LINES).join('\n') : text;
    const proposal = DOCUMENT_PROPOSAL.exec(question);
    return proposal ? `Propose improvements to ${proposal[1]}.` : question;
}
